import { Box, Button } from "@mui/material";
import React, { useContext, useState } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
// Typeform thumbs-down (kept for rollback)
// import "@typeform/embed/build/css/popup.css";
// import { createPopup } from "@typeform/embed";
import ShareIcon from "@mui/icons-material/Share";
import CheckIcon from "@mui/icons-material/Check";
import { trackThumbsFeedback } from "src/utils/analyticsCapFirebase";
import { useAuth0 } from "@auth0/auth0-react";
import { WidgetContext } from "src/contexts/WidgetContext";
import { isUnsyncedPlatform, UNSYNCED_INTEGRATION_NAME } from "src/constants/unsyncedLeague";
import ThumbsDownForm from "./ThumbsDownForm";
import { buildClientContext } from "src/utils/clientContext";
import { mapLeagueInfoToCalculatorSettings } from "src/utils/keeperNotes.utils";

const CALCULATOR_SUMMARY_KEYS = ["league_type", "team_size", "team_format", "team_type", "idp", "devy", "te_premium", "rb_premium"] as const;

function formatCalculatorSettings(settings: Record<string, unknown> | null | undefined): string {
  if (!settings || typeof settings !== "object") return "missing";
  const parts = CALCULATOR_SUMMARY_KEYS.filter((key) => settings[key] != null && settings[key] !== "").map((key) => `${key}=${settings[key]}`);
  return parts.join(", ") || "missing";
}

// Props interface to receive necessary data
interface ThumbsResponseProps {
  messageId?: string; //The message id from the response
  userId?: string; //The user id from the response
  message?: string; //Only for trades section, its the players trade from the response
  response?: any; //The response of the sectionfrom the response
  handleShare?: () => void; //The function to handle the share
  isCheckmark?: boolean; //The boolean to check if the device is desktop
  tradeResponseData?: any; //The response data from the trade section
  section?: "waiver" | "start" | "rundown" | "draft_advice" | "trade" | "chatbot" | "articles";
  platformId?: string;
  leagueId?: string;
  teamName?: string;
  shareUrl?: string; // POC share_url for the downvoted response (trade / agents)
}

const ThumbsResponse: React.FC<ThumbsResponseProps> = ({
  messageId,
  userId,
  message,
  response,
  handleShare,
  isCheckmark,
  tradeResponseData,
  section,
  platformId,
  leagueId,
  teamName,
  shareUrl,
}) => {
  // const {a} = useContext(WidgetContext);
  const { getAccessTokenSilently, isAuthenticated: auth0IsAuthenticated } = useAuth0();
  const { NON_LOGGED_IN_CONFIG, isLoggedInAuth0ExperienceGlobal, sectionData, selectedTeamInfo: contextTeamInfo, teamsDataCache, calculatorSettingsMap } = useContext(WidgetContext);
  //State to color thumbs
  const [thumbSelected, setThumbSelected] = useState<"up" | "down" | null>(null);
  const [isThumbsDownFormOpen, setIsThumbsDownFormOpen] = useState(false);
  const [accessAuthToken, setAccessAuthToken] = useState<string | null>(null);
  const [clientDebug, setClientDebug] = useState<{ client?: string; currentVersion?: string; deviceModel?: string }>({});
  // console.log("THUMBS PARAMS: messageId", messageId);
  // console.log("THUMBS PARAMS: userId", userId);
  // console.log("THUMBS PARAMS: message", message);
  // console.log("THUMBS PARAMS: response", response);
  // console.log("sectionData", sectionData);
  // console.log("contextTeamInfo", contextTeamInfo);
  // console.log("teamsDataCache", teamsDataCache);
  // console.log("platformId", platformId);
  // console.log("leagueId", leagueId);
  // console.log("teamName", teamName);

  // const POPUP_AUTO_CLOSE_DELAY_MS = 2000; // 2 seconds

  const handleThumbsUpClick = (e) => {
    e.stopPropagation();
    setThumbSelected(thumbSelected === "up" ? null : "up");
    // this looks like wrong but it correct because track event only on selection (null -> "up"), not on deselection
    if (thumbSelected !== "up") {
      trackThumbsFeedback(userId, section, "up", platformId, leagueId, teamName, messageId, typeof response === "string" ? response : JSON.stringify(response));
    }
  };

  const handleThumbsDownClick = async (e) => {
    e.stopPropagation();
    setThumbSelected(thumbSelected === "down" ? null : "down");

    // const popupId = "kogrC0Bz";
    // Open Typeform when thumbs down is clicked
    // this looks like wrong but it correct because track event only on selection (null -> "down"), not on deselection
    if (thumbSelected !== "down") {
      trackThumbsFeedback(userId, section, "down", platformId, leagueId, teamName, messageId, typeof response === "string" ? response : JSON.stringify(response));
      //Truncate the response to 1000 characters
      // const truncatedResponse = response ? (typeof response === "string" ? response : JSON.stringify(response)).substring(0, 1000) : "";
      // console.log("truncatedResponse", truncatedResponse);

      try {
        const token = auth0IsAuthenticated
          ? await getAccessTokenSilently()
          : !isLoggedInAuth0ExperienceGlobal
            ? NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_ACCESS_TOKEN
            : await getAccessTokenSilently();
        setAccessAuthToken(token);
        try {
          const ctx = await buildClientContext();
          setClientDebug({
            client: ctx.key,
            // Native: iOS-1.0.199. Web has no app version — say so instead of leaving Slack blank.
            currentVersion: ctx.currentVersion || (ctx.runtime === "web" ? "web (not ios/android)" : "unknown"),
            deviceModel: ctx.model,
          });
        } catch {
          setClientDebug({});
        }
        setIsThumbsDownFormOpen(true);
      } catch {
        setThumbSelected(null);
      }

      // Typeform popup (replaced by in-app ThumbsDownForm)
      // const popup = createPopup(popupId, {
      //     hidden: {
      //       user_id: userId, //The user id from the response
      //       message_id: messageId, //The message id from the response
      //       message: message, //Only for trades section, its the players trade from the response
      //       response: truncatedResponse, //The response of the sectionfrom the response
      //       // message_id: messageId,//The message id from the response
      //     },
      //     onSubmit: () => {
      //       // popup.close();
      //       console.log("Sent thumbs down response");
      //       // Auto-close popup after delay so user sees thank-you then it dismisses
      //       setTimeout(() => {
      //         popup.close();
      //         setThumbSelected(null)
      //       }, POPUP_AUTO_CLOSE_DELAY_MS);
      //     },
      //     // clearOnClose: true
      //   });
      //   popup.open();

      //   // handle the close button of the typeform
      //   const intervalId = setInterval(() => {
      //     const closeButton = document.querySelector(".tf-v1-close");
      //     if (closeButton) {
      //       closeButton.addEventListener("click", () => {
      //         setThumbSelected(null);
      //       });
      //       clearInterval(intervalId);
      //     }
      //   }, 1000);

      //   // setThumbSelected(null);
      //   // Limpiar el intervalo si el componente se desmonta
      //   return () => {
      //     // setThumbSelected(null);
      //     clearInterval(intervalId);
      //   };
    }
  };

  const truncatedResponse = response ? (typeof response === "string" ? response : JSON.stringify(response)).substring(0, 1000) : "";

  // Hidden Slack field only. User never sees it. Omitted for Un-Synced.
  const leagueContext = (() => {
    const common = sectionData?.commonLeagueData;
    const team = contextTeamInfo;
    const platform = platformId || team?.platformId || common?.platform || common?.platform_id;
    if (isUnsyncedPlatform(platform) || String(platform || "").trim().toLowerCase() === UNSYNCED_INTEGRATION_NAME) {
      return undefined;
    }
    const leagueFromList = team
      ? teamsDataCache?.[team.platformId]?.leagues?.find((league) => league.league_id === team.leagueId)?.name
      : undefined;
    const leagueName = common?.league_info?.league_name || leagueFromList;
    const part = (value: unknown) => {
      const text = value == null ? "" : String(value).trim();
      return text || "unknown";
    };
    return [platform, leagueName].map(part).join(" / ");
  })();

  const team = contextTeamInfo;
  const teamKey = team ? `${team.platformId}-${team.leagueId}` : "";
  const mappedCalculatorSettings = (teamKey && calculatorSettingsMap?.[teamKey]) || null;
  const leagueInfo = sectionData?.commonLeagueData?.league_info;
  const calculatorSettingsSummary = formatCalculatorSettings(
    mappedCalculatorSettings || (leagueInfo ? mapLeagueInfoToCalculatorSettings(leagueInfo, "") : null),
  );

  console.log("leagueContext", leagueContext);

  const closeThumbsDownForm = () => {
    setIsThumbsDownFormOpen(false);
    setThumbSelected(null);
  };

  // console.log("THUMBS RESPONSE PROPS: messageId for section:", section, messageId);

  return (
    <Box
      // borderTop={`0.5px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`}
      // borderBottom={`0.5px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`}
      // border={`1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`}
      color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
      bgcolor={`${PureLightThemeCaaS.colors.alpha.black[100]}`}
      textAlign={"center"}
      className={styles.tradeTeam}
      mb={0.5}

    >
      <Button
        className={styles.tradeTeamName}
        variant="text"
        sx={{
          color: thumbSelected === "up" ? `#ff672d` : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
          bgcolor: "#030506",
          padding: "4.5px !important",
        }}
        onClick={handleThumbsUpClick}
      >
        <ThumbUpOffAltIcon sx={{ pr: 0.5 }} className={styles.thumbs} />
      </Button>
      <Button
        className={styles.tradeTeamName}
        variant="text"
        sx={{
          color: thumbSelected === "down" ? `#ff672d` : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
          bgcolor: "#030506",
          padding: "4.5px !important",
        }}
        onClick={handleThumbsDownClick}
      >
        <ThumbDownOffAltIcon sx={{ pr: 0.5 }} className={styles.thumbs} />
      </Button>
      {/* Renders the share button only if the messageId is provided */}
      {messageId && handleShare && (
        <Button onClick={handleShare} className={styles.tradeTeamName} variant="text" sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}`, bgcolor: "#030506", padding: "4.5px !important", }}>
          {isCheckmark ? <CheckIcon className={styles.thumbs} /> : <ShareIcon sx={{ pr: 0.5 }} className={styles.thumbs} />}
        </Button>
      )}
      <ThumbsDownForm
        open={isThumbsDownFormOpen}
        accessAuthToken={accessAuthToken}
        messageId={messageId}
        message={message}
        truncatedResponse={truncatedResponse}
        leagueContext={leagueContext}
        shareUrl={shareUrl}
        client={clientDebug.client}
        currentVersion={clientDebug.currentVersion}
        deviceModel={clientDebug.deviceModel}
        calculatorSettings={calculatorSettingsSummary}
        onClose={closeThumbsDownForm}
        onSubmitted={closeThumbsDownForm}
      />
    </Box>
  );
};

export default ThumbsResponse;
